import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRight, CheckCircle2, Loader2, Upload, X, ShieldCheck } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { estimationsAPI } from '../services/api';
import Seo from '../components/Seo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PHOTO_SLOTS = [
  { key: 'front', labelKey: 'estimate.photo.front', fallback: 'Avant' },
  { key: 'back', labelKey: 'estimate.photo.back', fallback: 'Arrière' },
  { key: 'side', labelKey: 'estimate.photo.side', fallback: 'Côté' },
  { key: 'interior', labelKey: 'estimate.photo.interior', fallback: 'Intérieur' },
];

const FUEL_OPTIONS = ['Essence', 'Diesel', 'Hybride', 'Électrique', 'GPL'];
const TRANSMISSION_OPTIONS = [
  { value: 'manual', label: 'Manuelle' },
  { value: 'automatic', label: 'Automatique' },
  { value: 'semi-automatic', label: 'Semi-automatique' },
];

const EstimateRequest = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [photos, setPhotos] = useState({});
  const [uploadingSlot, setUploadingSlot] = useState(null);
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    make: '', model: '', year: '', fuel: 'Essence', transmission: 'manual',
    mileage: '', desired_price: '', ctech_valid: false, carpass: false, extra_info: '',
  });

  const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const uploadPhoto = async (slotKey, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(t('estimate.error.notImage', 'Le fichier doit être une image'));
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error(t('estimate.error.tooBig', 'Image trop lourde (>15 Mo)'));
      return;
    }
    setUploadingSlot(slotKey);
    try {
      const sigRes = await axios.get(`${BACKEND_URL}/api/cloudinary/signature`);
      const { signature, timestamp, cloud_name, api_key, folder } = sigRes.data;
      const fd = new FormData();
      fd.append('file', file);
      fd.append('api_key', api_key);
      fd.append('timestamp', timestamp);
      fd.append('signature', signature);
      fd.append('folder', folder);
      const upRes = await axios.post(`https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`, fd);
      setPhotos((p) => ({ ...p, [slotKey]: upRes.data.secure_url }));
    } catch (err) {
      console.error('Upload error', err);
      toast.error(t('estimate.error.upload', "Échec de l'upload"));
    } finally {
      setUploadingSlot(null);
    }
  };

  const removePhoto = (slotKey) => {
    setPhotos((p) => { const next = { ...p }; delete next[slotKey]; return next; });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const photoUrls = PHOTO_SLOTS.map((s) => photos[s.key]).filter(Boolean);
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        make: form.make.trim(),
        model: form.model.trim(),
        year: form.year ? parseInt(form.year, 10) : null,
        fuel: form.fuel,
        transmission: form.transmission,
        mileage: parseInt(form.mileage, 10) || 0,
        desired_price: form.desired_price ? parseFloat(form.desired_price) : null,
        ctech_valid: !!form.ctech_valid,
        carpass: !!form.carpass,
        photos: photoUrls,
        extra_info: form.extra_info.trim() || null,
      };
      await estimationsAPI.create(payload);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || t('estimate.error.submit', "Échec de l'envoi."));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center px-6 pt-32 pb-20" data-testid="estimate-success-page">
        <div className="max-w-2xl text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-600 mb-8">
            <CheckCircle2 size={36} strokeWidth={1.8} />
          </div>
          <span className="section-eyebrow">{t('estimate.success.eyebrow', 'Demande envoyée')}</span>
          <h1 className="mt-4 text-3xl md:text-5xl font-bold text-slate-900 leading-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('estimate.success.title', 'Merci, nous avons bien reçu votre dossier.')}
          </h1>
          <p className="text-slate-600 text-base md:text-lg leading-relaxed mb-10">
            {t('estimate.success.body', "Notre équipe analyse votre demande et reviendra vers vous dans les plus brefs délais.")}
          </p>
          <button onClick={() => navigate('/')} className="btn-primary" data-testid="estimate-back-home">
            {t('estimate.success.back', "Retour à l'accueil")}
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F8FA] pt-24 lg:pt-28 pb-20" data-testid="estimate-page">
      <Seo title={t('seo.estimate.title')} description={t('seo.estimate.desc')} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <span className="section-eyebrow">{t('estimate.eyebrow', 'Vente accompagnée premium')}</span>
          <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('estimate.title', 'Faites estimer votre véhicule')}
          </h1>
          <p className="mt-4 text-slate-600 max-w-2xl">
            {t('estimate.subtitle', "Remplissez le formulaire ci-dessous. Notre équipe analyse votre dossier et revient vers vous rapidement.")}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="estimate-form">
          <Section title={t('estimate.section.contact', 'Vos coordonnées')}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={t('estimate.field.firstName', 'Prénom')} required>
                <input required value={form.first_name} onChange={(e) => update('first_name', e.target.value)} className="form-input" data-testid="estimate-first-name" />
              </Field>
              <Field label={t('estimate.field.lastName', 'Nom')} required>
                <input required value={form.last_name} onChange={(e) => update('last_name', e.target.value)} className="form-input" data-testid="estimate-last-name" />
              </Field>
              <Field label={t('estimate.field.email', 'E-mail')} required>
                <input type="email" required value={form.email} onChange={(e) => update('email', e.target.value)} className="form-input" data-testid="estimate-email" />
              </Field>
              <Field label={`${t('estimate.field.phone', 'Téléphone')} (${t('estimate.optional', 'optionnel')})`}>
                <input type="tel" value={form.phone} onChange={(e) => update('phone', e.target.value)} className="form-input" data-testid="estimate-phone" />
              </Field>
            </div>
          </Section>

          <Section title={t('estimate.section.vehicle', 'Le véhicule')}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={t('estimate.field.make', 'Marque')} required>
                <input required value={form.make} onChange={(e) => update('make', e.target.value)} placeholder="BMW, Audi, Mercedes…" className="form-input" data-testid="estimate-make" />
              </Field>
              <Field label={t('estimate.field.model', 'Modèle')} required>
                <input required value={form.model} onChange={(e) => update('model', e.target.value)} placeholder="M3, A4, C63…" className="form-input" data-testid="estimate-model" />
              </Field>
              <Field label={t('estimate.field.year', 'Année')}>
                <input type="number" min="1950" max="2030" value={form.year} onChange={(e) => update('year', e.target.value)} className="form-input" data-testid="estimate-year" />
              </Field>
              <Field label={t('estimate.field.mileage', 'Kilométrage (km)')} required>
                <input type="number" required min="0" value={form.mileage} onChange={(e) => update('mileage', e.target.value)} className="form-input" data-testid="estimate-mileage" />
              </Field>
              <Field label={t('estimate.field.fuel', 'Carburant')} required>
                <select required value={form.fuel} onChange={(e) => update('fuel', e.target.value)} className="form-input" data-testid="estimate-fuel">
                  {FUEL_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </Field>
              <Field label={t('estimate.field.transmission', 'Boîte de vitesses')} required>
                <select required value={form.transmission} onChange={(e) => update('transmission', e.target.value)} className="form-input" data-testid="estimate-transmission">
                  {TRANSMISSION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </Field>
              <Field label={`${t('estimate.field.desiredPrice', 'Prix souhaité (€)')} (${t('estimate.optional', 'optionnel')})`}>
                <input type="number" min="0" step="100" value={form.desired_price} onChange={(e) => update('desired_price', e.target.value)} placeholder="ex. 18 500" className="form-input" data-testid="estimate-desired-price" />
              </Field>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <CheckboxRow checked={form.ctech_valid} onChange={(v) => update('ctech_valid', v)} label={t('estimate.field.ctech', 'Contrôle technique valide')} testId="estimate-ctech" />
              <CheckboxRow checked={form.carpass} onChange={(v) => update('carpass', v)} label={t('estimate.field.carpass', 'Car-Pass disponible')} testId="estimate-carpass" />
            </div>
          </Section>

          <Section title={t('estimate.section.photos', 'Photos du véhicule')}>
            <p className="text-slate-500 text-sm mb-5 leading-relaxed">
              {t('estimate.photos.help', 'Téléchargez idéalement une photo de chaque face. JPG/PNG, max 15 Mo par photo.')}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {PHOTO_SLOTS.map((slot) => (
                <PhotoSlot
                  key={slot.key}
                  label={t(slot.labelKey, slot.fallback)}
                  url={photos[slot.key]}
                  uploading={uploadingSlot === slot.key}
                  onUpload={(file) => uploadPhoto(slot.key, file)}
                  onRemove={() => removePhoto(slot.key)}
                  testId={`estimate-photo-${slot.key}`}
                />
              ))}
            </div>
          </Section>

          <Section title={t('estimate.section.extra', 'Informations complémentaires')}>
            <textarea
              value={form.extra_info}
              onChange={(e) => update('extra_info', e.target.value)}
              placeholder={t('estimate.extra.placeholder', "Historique d'entretien, équipements, particularités…")}
              rows={5}
              className="form-input resize-none"
              data-testid="estimate-extra"
            />
          </Section>

          <div className="flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-100 rounded-xl">
            <ShieldCheck size={18} className="text-emerald-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-slate-700">
              {t('estimate.disclaimer', "En envoyant cette demande, vous acceptez d'être recontacté par AutoDistrict concernant l'évaluation de votre véhicule. Vos données sont protégées — voir notre politique de confidentialité.")}
            </p>
          </div>

          <button type="submit" disabled={submitting} className="btn-primary" data-testid="estimate-submit">
            {submitting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                {t('estimate.submitting', 'Envoi en cours…')}
              </>
            ) : (
              <>
                {t('estimate.submit', 'Envoyer ma demande')}
                <ArrowRight size={16} />
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-7">
    <h2 className="text-slate-900 text-lg md:text-xl font-bold mb-5 pb-3 border-b border-slate-100" style={{ fontFamily: 'Outfit, sans-serif' }}>
      {title}
    </h2>
    {children}
  </div>
);

const Field = ({ label, required, children }) => (
  <div>
    <label className="form-label">
      {label}
      {required && <span className="text-emerald-600 ml-1">*</span>}
    </label>
    {children}
  </div>
);

const CheckboxRow = ({ checked, onChange, label, testId }) => (
  <label
    className="flex items-center gap-3 p-3 border border-slate-200 bg-slate-50 hover:bg-white hover:border-blue-200 rounded-xl cursor-pointer transition-colors"
    data-testid={`${testId}-row`}
  >
    <Checkbox
      checked={checked}
      onCheckedChange={onChange}
      className="data-[state=checked]:bg-blue-900 data-[state=checked]:border-blue-900"
      data-testid={testId}
    />
    <span className="text-slate-800 text-sm font-medium">{label}</span>
  </label>
);

const PhotoSlot = ({ label, url, uploading, onUpload, onRemove, testId }) => {
  const inputId = `photo-${testId}`;
  return (
    <div className="relative group">
      <input
        id={inputId}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ''; }}
        data-testid={`${testId}-input`}
      />
      <label
        htmlFor={inputId}
        className={`block aspect-[4/3] rounded-xl border-2 ${
          url ? 'border-blue-900' : 'border-dashed border-slate-300 hover:border-blue-900'
        } bg-white cursor-pointer transition-colors overflow-hidden`}
        data-testid={testId}
      >
        {url ? (
          <img src={url} alt={label} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-slate-500 gap-2">
            {uploading ? (
              <Loader2 size={20} className="animate-spin text-blue-900" />
            ) : (
              <Upload size={18} className="text-blue-900" />
            )}
            <span className="text-xs font-medium">{label}</span>
          </div>
        )}
      </label>
      {url && !uploading && (
        <button
          type="button"
          onClick={onRemove}
          className="absolute top-2 right-2 bg-white hover:bg-red-600 hover:text-white text-slate-700 rounded-full p-1.5 shadow-md opacity-0 group-hover:opacity-100 transition-all"
          data-testid={`${testId}-remove`}
          aria-label="Remove"
        >
          <X size={12} />
        </button>
      )}
      {url && (
        <span className="absolute bottom-2 left-2 bg-white/95 text-slate-900 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md">
          {label}
        </span>
      )}
    </div>
  );
};

export default EstimateRequest;
